<script setup>
import { computed, ref, useAttrs } from 'vue'

defineProps({
    type: {
        type: String,
        default: 'text',
    },
    modelValue: {
        type: [String, Number],
        default: '',
    },
})

const emit = defineEmits(['update:modelValue'])

// Class and style stay on the wrapper, where a parent's scoped rules expect
// them; everything else — listeners included — belongs on the input itself.
defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const showPassword = ref(false)

const inputAttrs = computed(() => {
    const { class: className, style, ...rest } = attrs
    return rest
})

function togglePasswordVisibility() {
    showPassword.value = !showPassword.value
}

function onInput(event) {
    emit('update:modelValue', event.target.value)
}
</script>

<template>
    <div
        class="base-input"
        :class="$attrs.class"
        :style="$attrs.style"
    >
        <input
            :type="showPassword ? 'text' : type"
            :value="modelValue"
            class="input"
            v-bind="inputAttrs"
            @input="onInput"
        >
        <div
            v-if="type === 'password'"
            class="password-toggle"
            @click="togglePasswordVisibility"
        >
            <BaseIcon
                class="eye-icon"
                :name="showPassword ? 'eye-open' : 'eye-closed'"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.input {
    @extend %input;
}

.base-input {
    position: relative;

    &.error .input {
        background-color: transparentize($c-status-error, 0.95);
    }
}

.password-toggle {
    position: absolute;
    top: 0;
    right: $s-m;
    display: flex;
    align-items: center;
    height: $size-input-height;
    cursor: pointer;
}

.eye-icon {
    max-width: 22px;
    max-height: 22px;
    color: $c-text-faded;
    opacity: 0.5;
}
</style>
